/**
 * Which items a canvas hydration writes into their atoms.
 *
 * The canvas hydrates its item atoms from the page JSON (with the Lotus
 * bundle, the drafts and the pending deletes merged in) and hydrates again
 * whenever any of those inputs changes — the bundle arriving after the
 * page did, a draft committing. Between two hydrations an atom can have
 * moved on its own: a text box's load result is written into it as an
 * annotation, an author's edit lands in it. A re-hydration that wrote every
 * atom differing from its JSON would undo those, and did: a box grown to
 * its ceiling on load was reset to its stored height when the bundle came.
 *
 * So a hydration writes an atom when the atom holds nothing, when it holds
 * what the JSON says already (no write needed), or when the JSON item
 * itself changed since the last hydration — the JSON is what changed, and
 * it wins. An atom that differs from an unchanged JSON item moved on its
 * own, and is kept.
 */
export type HydrationMemory = Map<string, string>

type HydratedItem = { id: string; type?: string | null }

const jsonOf = (value: unknown): string => JSON.stringify(value)

const holdsAnItem = (current: unknown): boolean =>
  typeof current === 'object' &&
  current !== null &&
  typeof (current as HydratedItem).type === 'string'

export const hydrationWrites = <T extends HydratedItem>(
  hydrated: readonly T[],
  memory: HydrationMemory,
  currentOf: (id: string) => unknown,
): T[] => {
  const writes: T[] = []
  const seen = new Set<string>()
  for (const item of hydrated) {
    seen.add(item.id)
    const json = jsonOf(item)
    const last = memory.get(item.id)
    memory.set(item.id, json)
    const current = currentOf(item.id)
    if (!holdsAnItem(current)) {
      writes.push(item)
      continue
    }
    if (jsonOf(current) === json) continue
    if (last === json) continue
    writes.push(item)
  }
  for (const id of Array.from(memory.keys())) {
    if (!seen.has(id)) memory.delete(id)
  }
  return writes
}
